"use client";

import type { Video as GeneratedVideoProps } from "@homeinstead/schemas";

import { VideoPlayer } from "@homeinstead/ui/components/VideoPlayer";
import { useMemo } from "react";
import { usePageBuilderCookieConsent } from "~www/components/PageBuilder/page-builder-runtime-context";
import { urlFor } from "~www/lib/sanity/sanity-images";
import { buildVideoObjectFromLegacyVideo } from "~www/lib/videos/build-video-object-from-block";
import { getVimeoId } from "~www/lib/videos/resolve-video-source-urls";
import { prepareVideoURL } from "~www/lib/videos/video-utils";
import { TextContent } from "./TextContent";
import { VideoSchema } from "./VideoSchema";

type VideoProps = Omit<GeneratedVideoProps, "_type">;

function Video({
  description,
  durationSeconds,
  name,
  regionsAllowed,
  source,
  thumbnail,
  title,
  uploadDate,
}: VideoProps) {
  const cookieConsent = usePageBuilderCookieConsent();
  const thumbnailUrl = thumbnail?.image?.asset?._ref
    ? urlFor(thumbnail.image.asset).url()
    : undefined;

  const videoAssetId = source;

  const videoSrc = useMemo(
    () => (source ? prepareVideoURL(source, cookieConsent) : undefined),
    [cookieConsent, source],
  );

  const videoObject = useMemo(
    () =>
      buildVideoObjectFromLegacyVideo({
        description,
        durationSeconds,
        name,
        regionsAllowed,
        source,
        thumbnail,
        title,
        uploadDate,
      }),
    [
      description,
      durationSeconds,
      name,
      regionsAllowed,
      source,
      thumbnail,
      title,
      uploadDate,
    ],
  );

  const schemaId = getVimeoId(source) ?? source;

  return (
    <>
      {videoObject ? (
        <VideoSchema schemaId={schemaId} videoData={videoObject} />
      ) : null}
      <div className="flex flex-col gap-2">
        <div className="aspect-video w-full">
          <VideoPlayer
            contentAssetId={videoAssetId}
            controls
            light={thumbnailUrl}
            src={videoSrc}
          />
        </div>

        <TextContent {...title} placeholders={{}} />
      </div>
    </>
  );
}

export { Video };
